/**
 * Onde o custo está concentrado.
 *
 * Forma: barra horizontal (magnitude com nomes longos) na variante de ÊNFASE —
 * os itens que somam os primeiros 80% do custo recebem o tom de acento, o resto
 * fica no cinza de segundo plano. Não é rampa de valor: colorir cada barra mais
 * escura conforme o tamanho duplicaria o comprimento em cor e gastaria o único
 * canal livre com informação que a barra já mostra.
 *
 * O percentual acumulado aparece como TEXTO, nunca como segunda escala. Gráfico
 * de dois eixos inventa correlação que não está no dado.
 */

import React, { useState } from "react";
import { Cartao, SemDados, caminhoBarra, moeda, percentual, useDica } from "./viz";
import type { Insights } from "@utils/planilha-insights";

type Linha = Insights["pareto"][number];

const ALTURA_BARRA = 22; // ≤ 24px por especificação; a sobra da faixa é ar
const ALTURA_FAIXA = 34;
const LARGURA_ROTULO = 210;
/**
 * Faixa reservada à direita para o valor na ponta da barra e o acumulado.
 * Dimensionada para a barra mais longa: com plot de 460px o rótulo do maior
 * item invadia o acumulado em 10px. Rótulo nunca é cortado nem sobreposto.
 */
const LARGURA_PLOT = 420;
const LARGURA_VALOR = 170;

export const ParetoItens: React.FC<{ insights: Insights }> = ({ insights }) => {
  const [verTabela, setVerTabela] = useState(false);
  const { mostrar, esconder, Dica } = useDica<Linha>();

  const linhas = insights.pareto.slice(0, 12);
  if (linhas.length === 0) {
    return (
      <Cartao titulo="Onde o custo está">
        <SemDados>Nenhum item com valor preenchido nesta planilha.</SemDados>
      </Cartao>
    );
  }

  const maior = Math.max(...linhas.map((l) => l.total));
  const larguraPlot = LARGURA_PLOT;
  const altura = linhas.length * ALTURA_FAIXA + 26;
  const escala = (v: number) => (maior > 0 ? (v / maior) * larguraPlot : 0);

  const dentroDo80 = insights.itensAte80Porcento;
  const marcas = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maior);

  return (
    <Cartao
      titulo="Onde o custo está"
      descricao={`${dentroDo80} ${dentroDo80 === 1 ? "item concentra" : "itens concentram"} 80% do orçamento. É neles que negociar rende mais.`}
      acao={
        <button
          className="btn btn-ghost btn-xs"
          onClick={() => setVerTabela((v) => !v)}
          aria-pressed={verTabela}
        >
          {verTabela ? "Ver gráfico" : "Ver tabela"}
        </button>
      }
    >
      {/* Legenda sempre presente: são duas séries, e a identidade não pode
          depender só da cor. */}
      <ul className="mb-3 flex flex-wrap gap-4 text-xs text-base-content/70">
        <li className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: "var(--viz-accent)" }}
          />
          Dentro dos primeiros 80%
        </li>
        <li className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: "var(--viz-neutral)" }}
          />
          Cauda
        </li>
      </ul>

      {verTabela ? (
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Item</th>
                <th className="text-right">Total</th>
                <th className="text-right">Fatia</th>
                <th className="text-right">Acumulado</th>
              </tr>
            </thead>
            <tbody>
              {insights.pareto.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.numero} — {l.descricao}
                  </td>
                  <td className="text-right tabular-nums">{moeda(l.total)}</td>
                  <td className="text-right tabular-nums">{percentual(l.fracao, 1)}</td>
                  <td className="text-right tabular-nums">
                    {percentual(l.acumulado, 1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative overflow-x-auto">
          <svg
            width={LARGURA_ROTULO + larguraPlot + LARGURA_VALOR}
            height={altura}
            role="img"
            aria-label="Total por item, do maior para o menor, com percentual acumulado"
          >
            {/* Grade em hairline sólido, recessiva */}
            {marcas.map((m, i) => (
              <g key={i}>
                <line
                  x1={LARGURA_ROTULO + escala(m)}
                  x2={LARGURA_ROTULO + escala(m)}
                  y1={0}
                  y2={altura - 26}
                  stroke="var(--viz-grid)"
                  strokeWidth={1}
                />
                <text
                  x={LARGURA_ROTULO + escala(m)}
                  y={altura - 10}
                  textAnchor="middle"
                  className="text-[10px]"
                  fill="var(--viz-muted)"
                >
                  {m >= 1000 ? `${Math.round(m / 1000)} mil` : Math.round(m)}
                </text>
              </g>
            ))}

            {/* Linha de base única de onde as barras crescem */}
            <line
              x1={LARGURA_ROTULO}
              x2={LARGURA_ROTULO}
              y1={0}
              y2={altura - 26}
              stroke="var(--viz-axis)"
              strokeWidth={1}
            />

            {linhas.map((l, i) => {
              const y = i * ALTURA_FAIXA + (ALTURA_FAIXA - ALTURA_BARRA) / 2;
              const largura = escala(l.total);
              const emEnfase = i < dentroDo80;

              return (
                <g
                  key={l.id}
                  onMouseMove={(e) => mostrar(e, l)}
                  onMouseLeave={esconder}
                  tabIndex={0}
                  onFocus={(e) => mostrar(e as any, l)}
                  onBlur={esconder}
                >
                  {/* Alvo de mouse maior que a marca */}
                  <rect
                    x={0}
                    y={i * ALTURA_FAIXA}
                    width={LARGURA_ROTULO + larguraPlot + LARGURA_VALOR}
                    height={ALTURA_FAIXA}
                    fill="transparent"
                  />
                  <text
                    x={LARGURA_ROTULO - 10}
                    y={y + ALTURA_BARRA / 2 + 4}
                    textAnchor="end"
                    className="text-[11px]"
                    fill="var(--viz-ink-2)"
                  >
                    {l.numero} {l.descricao.slice(0, 24)}
                    {l.descricao.length > 24 ? "…" : ""}
                  </text>
                  <path
                    d={caminhoBarra(LARGURA_ROTULO, y, largura, ALTURA_BARRA)}
                    fill={emEnfase ? "var(--viz-accent)" : "var(--viz-neutral)"}
                  />
                  {/* Valor na ponta da barra, fora dela — nunca cortado */}
                  <text
                    x={LARGURA_ROTULO + largura + 8}
                    y={y + ALTURA_BARRA / 2 + 4}
                    className="text-[11px] tabular-nums"
                    fill="var(--viz-ink)"
                  >
                    {moeda(l.total, true)}
                  </text>
                  <text
                    x={LARGURA_ROTULO + larguraPlot + LARGURA_VALOR - 4}
                    y={y + ALTURA_BARRA / 2 + 4}
                    textAnchor="end"
                    className="text-[10px] tabular-nums"
                    fill="var(--viz-muted)"
                  >
                    {percentual(l.acumulado)} acum.
                  </text>
                </g>
              );
            })}
          </svg>

          <Dica>
            {(l) => (
              <>
                <p className="font-semibold text-base-content">
                  {l.numero} — {l.descricao}
                </p>
                <dl className="mt-1 space-y-0.5 text-base-content/70">
                  <div className="flex justify-between gap-4">
                    <dt>Total</dt>
                    <dd className="tabular-nums">{moeda(l.total)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt>Fatia do orçamento</dt>
                    <dd className="tabular-nums">{percentual(l.fracao, 1)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt>Quantidade × diárias</dt>
                    <dd className="tabular-nums">
                      {l.quantidade} × {l.diaria}
                    </dd>
                  </div>
                  {l.vencedora && (
                    <div className="flex justify-between gap-4">
                      <dt>Vencedor</dt>
                      <dd>{l.vencedora.fornecedorNome}</dd>
                    </div>
                  )}
                </dl>
              </>
            )}
          </Dica>
        </div>
      )}

      {insights.pareto.length > linhas.length && (
        <p className="mt-3 text-xs text-base-content/60">
          Mostrando os {linhas.length} maiores de {insights.pareto.length} itens. Os
          demais estão na tabela.
        </p>
      )}
    </Cartao>
  );
};
